"use client";

import { useEffect, useId } from "react";

import { CalendarIcon } from "lucide-react";

import {
  PartyProfileFields,
  ProfileFieldLabel,
  ProfileTextInput,
} from "@/components/backoffice/cat/vehicle-assignment/client-step/party-profile-fields";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldContent, FieldError } from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { isIndividualEditComplete } from "@/lib/cat/draft-client-mapper";
import {
  EMPTY_EDITABLE_INDIVIDUAL_PROFILE,
  type EditableIndividualProfile,
} from "@/lib/cat/individual-profile.types";
import { validateIndividualIdentity } from "@/lib/cat/party-profile-validation";

const uiText = {
  placeholders: {
    birthDate: "aaaa-mm-jj",
  },
  labels: {
    birthDate: "Date de naissance",
    firstName: "Prénom",
    lastName: "Nom",
  },
};

type RetailIndividualCreateValues = EditableIndividualProfile & {
  birthDate: string;
  firstName: string;
  lastName: string;
};

const defaultValues: RetailIndividualCreateValues = {
  ...EMPTY_EDITABLE_INDIVIDUAL_PROFILE,
  birthDate: "",
  firstName: "",
  lastName: "",
};

function RetailIndividualCreateForm({
  onCanProceedChange,
  onChange,
  value,
}: {
  onCanProceedChange?: (canProceed: boolean) => void;
  onChange: (value: RetailIndividualCreateValues) => void;
  value: RetailIndividualCreateValues;
}) {
  const idPrefix = useId();

  const identityErrors = validateIndividualIdentity(value);
  const canProceed = isIndividualEditComplete(value);

  useEffect(() => {
    onCanProceedChange?.(canProceed);
  }, [canProceed, onCanProceedChange]);

  const patch = (partial: Partial<RetailIndividualCreateValues>) =>
    onChange({ ...value, ...partial });

  return (
    <Card className="border-muted">
      <CardContent className="pt-6">
        <div className="space-y-4">
          <p className="text-muted-foreground text-sm">
            Les champs marqués d&apos;un{" "}
            <span className="text-destructive">*</span> sont obligatoires.
          </p>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field data-invalid={identityErrors.lastName ? true : undefined}>
              <ProfileFieldLabel
                htmlFor={`${idPrefix}-lastName`}
                label={uiText.labels.lastName}
                required
              />
              <FieldContent>
                <ProfileTextInput
                  id={`${idPrefix}-lastName`}
                  name={`${idPrefix}-last-name`}
                  autoComplete="family-name"
                  locked={false}
                  invalid={Boolean(identityErrors.lastName)}
                  value={value.lastName}
                  onChange={(lastName) => patch({ lastName })}
                />
                {identityErrors.lastName ? (
                  <FieldError>{identityErrors.lastName}</FieldError>
                ) : null}
              </FieldContent>
            </Field>
            <Field data-invalid={identityErrors.firstName ? true : undefined}>
              <ProfileFieldLabel
                htmlFor={`${idPrefix}-firstName`}
                label={uiText.labels.firstName}
              />
              <FieldContent>
                <ProfileTextInput
                  id={`${idPrefix}-firstName`}
                  name={`${idPrefix}-first-name`}
                  autoComplete="given-name"
                  locked={false}
                  invalid={Boolean(identityErrors.firstName)}
                  value={value.firstName}
                  onChange={(firstName) => patch({ firstName })}
                />
                {identityErrors.firstName ? (
                  <FieldError>{identityErrors.firstName}</FieldError>
                ) : null}
              </FieldContent>
            </Field>

            <Field className="md:col-span-2">
              <ProfileFieldLabel
                htmlFor={`${idPrefix}-birthDate`}
                label={uiText.labels.birthDate}
              />
              <FieldContent>
                <InputGroup>
                  <InputGroupInput
                    id={`${idPrefix}-birthDate`}
                    name={`${idPrefix}-birth-date`}
                    autoComplete="bday"
                    placeholder={uiText.placeholders.birthDate}
                    value={value.birthDate}
                    onChange={(e) => patch({ birthDate: e.target.value })}
                  />
                  <InputGroupAddon align="inline-end">
                    <CalendarIcon className="text-muted-foreground" />
                  </InputGroupAddon>
                </InputGroup>
              </FieldContent>
            </Field>
          </div>

          <PartyProfileFields
            idPrefix={`${idPrefix}-party`}
            value={value}
            onChange={(party) => onChange({ ...value, ...party })}
            showValidation
            requireEmailOrPhone
          />
        </div>
      </CardContent>
    </Card>
  );
}

export type { RetailIndividualCreateValues };
export {
  RetailIndividualCreateForm,
  defaultValues as retailIndividualCreateDefaults,
};
